import { useRef, useState } from 'react'
import { formatMoney, parseMoney } from '../../lib/money'
import { back } from '../../lib/router'
import { useStore } from '../../store'
import { Card, Chip, Dialog, Switch, TopBar } from '../../ui/parts'
import {
  addEarnAction,
  deleteEarnAction,
  exportJson,
  importJson,
  setEarnEnabled,
  setMonthlyAllowance,
} from '../repo'
import { countdown } from './EarnActionCard'

/**
 * Not a security boundary — anyone with the URL can read this in the bundle. It only
 * stops the phone's owner wandering into the screen that hands out budget.
 */
const ADMIN_PASSCODE = '4321'

export function SettingsScreen() {
  const { snapshot, account, mutate, logout, showToast } = useStore()
  const [allowance, setAllowance] = useState(() =>
    snapshot ? formatMoney(snapshot.settings.monthlyAllowanceFils) : ''
  )
  const [passcode, setPasscode] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [pendingImport, setPendingImport] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  if (!snapshot) return null

  const parsedAllowance = parseMoney(allowance)

  function download() {
    const blob = new Blob([exportJson(snapshot!)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `budget-backup-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
    showToast('Backup downloaded')
  }

  return (
    <div className="screen">
      <TopBar title="Settings" onBack={back} />

      <div className="stack">
        <Card>
          <div style={{ fontWeight: 650 }}>Monthly budget</div>
          <label className="field" style={{ marginTop: 10 }}>
            <span>Amount in AED</span>
            <input
              type="text"
              inputMode="decimal"
              value={allowance}
              className={parsedAllowance === null || parsedAllowance <= 0 ? 'error' : ''}
              onChange={(e) => setAllowance(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="btn small"
            disabled={parsedAllowance === null || parsedAllowance <= 0}
            onClick={async () => {
              const value = parsedAllowance!
              if (await mutate((s) => setMonthlyAllowance(s, value))) {
                showToast(`Monthly budget set to ${formatMoney(value)} AED`)
              }
            }}
          >
            Save
          </button>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Applies to every month, including ones already passed, so past balances are
            recalculated.
          </div>
        </Card>

        <Card>
          <div className="row">
            <div className="grow">
              <div style={{ fontWeight: 650 }}>Earn additional budget</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Show tasks on the budget screen that pay extra budget when completed.
              </div>
            </div>
            <Switch
              checked={snapshot.settings.earnEnabled}
              label="Earn additional budget"
              onChange={(value) => void mutate((s) => setEarnEnabled(s, value))}
            />
          </div>

          {snapshot.settings.earnEnabled ? (
            <div style={{ borderTop: '1px solid var(--hairline)', marginTop: 14, paddingTop: 14 }}>
              {isAdmin ? (
                <AdminPanel onLock={() => setIsAdmin(false)} />
              ) : (
                <>
                  <div style={{ fontWeight: 650 }}>Admin</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    Enter the passcode to create actions.
                  </div>
                  <div className="row" style={{ marginTop: 10 }}>
                    <input
                      type="password"
                      inputMode="numeric"
                      placeholder="Passcode"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn small"
                      disabled={passcode === ''}
                      onClick={() => {
                        if (passcode === ADMIN_PASSCODE) {
                          setIsAdmin(true)
                          setPasscode('')
                          showToast('Admin mode unlocked')
                        } else {
                          showToast('Wrong passcode')
                        }
                      }}
                    >
                      Unlock
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </Card>

        <Card>
          <div style={{ fontWeight: 650 }}>Backup</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            Everything is stored in this browser. Clearing site data deletes your history, so
            export a copy now and then.
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button type="button" className="btn outline small" onClick={download}>
              Export data
            </button>
            <button
              type="button"
              className="btn ghost small"
              onClick={() => fileInput.current?.click()}
            >
              Import
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="application/json"
              hidden
              onChange={async (e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (!file) return
                setPendingImport(await file.text())
              }}
            />
          </div>
        </Card>

        <Card>
          <div className="row">
            <div className="grow">
              <div style={{ fontWeight: 650 }}>Signed in as {account?.name ?? '—'}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                Your data is tied to the access code you entered.
              </div>
            </div>
            <button type="button" className="btn outline small" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        </Card>

        <div className="muted" style={{ fontSize: 12, padding: '0 4px 20px' }}>
          Budget resets on the 1st, Asia/Dubai.
        </div>
      </div>

      {pendingImport ? (
        <Dialog
          title="Replace all data?"
          body="Importing wipes every item, purchase and log entry in this browser and replaces them with the contents of the file. This cannot be undone."
          confirmLabel="Replace everything"
          danger
          onCancel={() => setPendingImport(null)}
          onConfirm={async () => {
            const text = pendingImport
            setPendingImport(null)
            try {
              const restored = importJson(text)
              if (await mutate(() => restored)) showToast('Data restored')
            } catch {
              showToast('That file could not be read as a backup')
            }
          }}
        />
      ) : null}
    </div>
  )
}

function AdminPanel({ onLock }: { onLock: () => void }) {
  const { snapshot, mutate, showToast, tick } = useStore()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [reward, setReward] = useState('')
  const [days, setDays] = useState('7')
  const [repeatable, setRepeatable] = useState(false)

  if (!snapshot) return null

  const parsedReward = parseMoney(reward)
  const parsedDays = days === '' ? null : Number(days)
  const daysValid = repeatable || (parsedDays !== null && parsedDays > 0)
  const canSave = title.trim() !== '' && parsedReward !== null && parsedReward > 0 && daysValid

  // `tick` keeps the countdowns in this list moving.
  void tick

  return (
    <>
      <div className="row between">
        <div style={{ fontWeight: 650 }}>Admin — new action</div>
        <button type="button" className="btn ghost small" onClick={onLock}>
          Lock
        </button>
      </div>

      <label className="field" style={{ marginTop: 10 }}>
        <span>Title</span>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>

      <label className="field">
        <span>Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>

      <label className="field">
        <span>Reward in AED</span>
        <input
          type="text"
          inputMode="decimal"
          value={reward}
          className={reward !== '' && (parsedReward === null || parsedReward <= 0) ? 'error' : ''}
          onChange={(e) => setReward(e.target.value)}
        />
      </label>

      <label className="field">
        <span>Available for (days)</span>
        <input
          type="text"
          inputMode="numeric"
          value={days}
          disabled={repeatable}
          className={!repeatable && days !== '' && !daysValid ? 'error' : ''}
          onChange={(e) => setDays(e.target.value.replace(/\D/g, '').slice(0, 3))}
        />
      </label>

      <div className="row" style={{ marginBottom: 12 }}>
        <div className="grow">
          <div style={{ fontSize: 14 }}>Never expires, can be repeated</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Stays on the list after completing and pays out every time.
          </div>
        </div>
        <Switch checked={repeatable} onChange={setRepeatable} label="Repeatable" />
      </div>

      <button
        type="button"
        className="btn block"
        disabled={!canSave}
        onClick={async () => {
          const saved = await mutate((s) =>
            addEarnAction(s, {
              title,
              description,
              rewardFils: parsedReward!,
              expiresInDays: repeatable ? null : parsedDays,
              repeatable,
            })
          )
          if (saved) {
            setTitle('')
            setDescription('')
            setReward('')
            showToast(repeatable ? 'Repeatable action added' : `Action added — ${parsedDays} days`)
          }
        }}
      >
        Add action
      </button>

      {snapshot.actions.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 650, marginBottom: 6 }}>All actions</div>
          {snapshot.actions.map((action) => (
            <div className="row" key={action.id} style={{ padding: '6px 0' }}>
              <div className="grow">
                <div style={{ fontSize: 14 }}>{action.title}</div>
                <div className="label">
                  {action.repeatable
                    ? `Repeatable · completed ${action.completedCount}×`
                    : action.completedAt
                      ? 'Completed'
                      : action.expiresAt
                        ? `Live · ${countdown(new Date(action.expiresAt).getTime() - Date.now())}`
                        : 'Live · never expires'}
                </div>
              </div>
              <Chip tone="mint">{formatMoney(action.rewardFils)}</Chip>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => void mutate((s) => deleteEarnAction(s, action.id))}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </>
  )
}
